import { Link } from 'react-router-dom'
import WireframeBanner from './WireframeBanner'
import { PageDesignNote } from './designNotes'
import PageMasthead from '../../components/PageMasthead'
import { ORGANISATIONS } from './mockData'
import { OrgStatus } from './types'
import { WIREFRAME_PATHS } from './routes'
import { monogram } from '../../util/monogram'

// Status pill colours reuse the semantic palette (safe / suspect / severe).
const STATUS_STYLES: Record<OrgStatus, string> = {
    Active: 'bg-safe/10 text-safe',
    Onboarding: 'bg-suspect/10 text-suspect',
    Suspended: 'bg-severe/10 text-severe'
}

// Matching dot colour so status is legible without relying on colour alone.
const STATUS_DOT: Record<OrgStatus, string> = {
    Active: 'bg-safe',
    Onboarding: 'bg-suspect',
    Suspended: 'bg-severe'
}

// Neutral ink monogram — a stable identity mark per organisation. Kept monochrome
// (no rainbow, no chrome red) in line with the Arwen system.
const MONOGRAM_TINTS = ['bg-gray-200 text-gray-800']

export default function OrgListPage() {
    return (
        <div className="flex min-h-0 w-full flex-1 flex-col bg-gray-50">
            <WireframeBanner />

            <PageDesignNote replaces="the Teams admin list" reachableFrom="the Admin sidebar">
                Organisations become the top-level grouping above teams and profiles. This list would
                eventually replace Team admin as the primary entry point — but not yet; the two sit
                side by side for now.
            </PageDesignNote>

            <PageMasthead
                title="Organisations"
                subtitle={`${ORGANISATIONS.length} organisations across the platform`}
                actions={
                    <button
                        type="button"
                        className="rounded-lg bg-arwen-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-arwen-700"
                    >
                        + Add organisation
                    </button>
                }
            />

            {/* Search (visual only) */}
            <div className="px-6 pb-3 pt-4">
                <span className="block max-w-sm rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-400">
                    Search organisations…
                </span>
            </div>

            {/* List */}
            <div className="flex-1 overflow-auto px-6 pb-6">
                <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <table className="w-full border-collapse text-sm">
                        <thead>
                            <tr className="border-b border-gray-100 bg-gray-50/60 text-left">
                                {['Organisation', 'Key contact', 'Plan', 'Teams', 'Profiles', 'Users', 'Status'].map(
                                    (h) => (
                                        <th
                                            key={h}
                                            scope="col"
                                            className="px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-wide text-gray-800"
                                        >
                                            {h}
                                        </th>
                                    )
                                )}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {ORGANISATIONS.map((org, i) => (
                                <tr key={org.id} className="hover:bg-gray-50/60">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2.5">
                                            <span
                                                className={`flex h-7 w-7 flex-none items-center justify-center rounded-md text-[11px] font-bold ${
                                                    MONOGRAM_TINTS[i % MONOGRAM_TINTS.length]
                                                }`}
                                                aria-hidden="true"
                                            >
                                                {monogram(org.name)}
                                            </span>
                                            <Link
                                                to={WIREFRAME_PATHS.orgConfig}
                                                className="font-semibold text-arwen-700 hover:underline"
                                            >
                                                {org.name}
                                            </Link>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-gray-800">{org.keyContact}</td>
                                    <td className="px-4 py-3 text-gray-800">{org.plan}</td>
                                    <td className="px-4 py-3 text-gray-800">{org.teamCount}</td>
                                    <td className="px-4 py-3 text-gray-800">
                                        {org.profileCount.toLocaleString()}
                                    </td>
                                    <td className="px-4 py-3 text-gray-800">{org.userCount}</td>
                                    <td className="px-4 py-3">
                                        <span
                                            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLES[org.status]}`}
                                        >
                                            <span
                                                className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[org.status]}`}
                                                aria-hidden="true"
                                            />
                                            {org.status}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
